// <snk-size-guide> : guide des tailles interactif « Ta taille, en 20 secondes » (snippets/snk-size-guide.liquid).
// Onglets Ma pointure (défaut) · Je mesure mon pied · Tableau, et un seul bloc de recommandation.
// Règles (spec LG du 30 oct. 2026, snk-backend/docs/guides-tailles.md) :
// - aucune recommandation tant que le visiteur n'a rien saisi (pointure dans « Ma pointure » ou curseur de « Je mesure
//   mon pied ») ; elle vient seulement de sa saisie, jamais du stock ni de la taille présélectionnée sur la fiche ;
// - longueur du pied → pointure du tableau de la marque ; entre deux pointures (chevauchement ou trou) la plus grande ;
//   sur une fiche, une pointure qui n'existe pas dans le modèle → « Pas dispo sur ce modèle » et lien vers les paires
//   dispo dans cette pointure, jamais une autre taille (Yannis, call du 4 nov. ; LG, 5 nov. 2026 : « 26 cm → 44 »
//   était un mauvais conseil) ;
// - taille recommandée épuisée : on le dit (« Ton 41 est épuisé sur ce modèle ») sans proposer une autre taille ;
// - hors tableau et marque sans tableau → écrire à la boutique.
// Tableau : une marque à la fois (celle du modèle par défaut), seulement ses lignes, colonnes cm · EU · BR · US · UK
// présentes dans ses données ; largeurs D / 2E (New Balance) dans le bloc « Pied large ? » sous le tableau.
// Les fonctions de calcul sont pures (testées par tests/snk-size-guide.test.mjs via module.exports).
(() => {
  // « 42.5 » → 42.5, « 42 2/3 » → 42.667 (tiers Adidas), sinon NaN
  const toNumber = (size) => {
    const m = String(size).trim().match(/^(\d+)(?:[.,](\d+)|\s+(\d)\/(\d))?$/);
    if (!m) return NaN;
    let n = Number(m[1]);
    if (m[2]) n += Number(`0.${m[2]}`);
    else if (m[3]) n += Number(m[3]) / Number(m[4]);
    return n;
  };

  const sortRows = (rows) =>
    (rows || [])
      .map((r) => ({ size: String(r.size), n: toNumber(r.size), min: Number(String(r.min).replace(',', '.')), max: Number(String(r.max).replace(',', '.')) }))
      .filter((r) => !Number.isNaN(r.n) && !Number.isNaN(r.min) && !Number.isNaN(r.max))
      .sort((a, b) => a.min - b.min || a.n - b.n);

  // Longueur (cm) → { size, between } ; null hors du tableau. Chevauchement : la plus grande ; trou : la ligne au-dessus.
  function snkSizeFromLength(rows, cm) {
    const sorted = sortRows(rows);
    if (!sorted.length || Number.isNaN(cm)) return null;
    const hits = sorted.filter((r) => cm >= r.min && cm <= r.max);
    if (hits.length) {
      const best = hits.reduce((a, b) => (b.n > a.n ? b : a));
      return { size: best.size, between: hits.length > 1 };
    }
    if (cm < sorted[0].min || cm > Math.max(...sorted.map((r) => r.max))) return null;
    const above = sorted.find((r) => r.min > cm);
    return above ? { size: above.size, between: true } : null;
  }

  // Pointure d'une marque → longueur (milieu de sa plage) ; null si la pointure n'est pas dans le tableau
  function snkLengthFromSize(rows, size) {
    const n = toNumber(size);
    const row = sortRows(rows).find((r) => r.n === n);
    return row ? Math.round(((row.min + row.max) / 2) * 10) / 10 : null;
  }

  // Pointure conseillée → pointure existant dans les variantes du modèle : exacte, sinon la taille au-dessus ; null si aucune.
  // La disponibilité n'intervient pas dans le choix : elle est seulement rapportée.
  function snkFitToModel(size, sizes) {
    const n = toNumber(size);
    const list = (sizes || [])
      .map((s) => ({ value: String(s.value), available: Boolean(s.available), last: Boolean(s.last), n: toNumber(s.value) }))
      .filter((s) => !Number.isNaN(s.n))
      .sort((a, b) => a.n - b.n);
    if (!list.length || Number.isNaN(n)) return null;
    const exact = list.find((s) => s.n === n);
    if (exact) return { size: exact.value, exists: true, available: exact.available, last: exact.last };
    const above = list.find((s) => s.n > n);
    return above ? { size: above.value, exists: false, wanted: String(size), available: above.available, last: above.last } : null;
  }

  // Ancienne règle « épuisée → la taille disponible au-dessus » : plus utilisée par le guide (spec LG du 30 oct. 2026),
  // gardée pour les tests existants
  function snkNextAvailable(size, sizes) {
    const n = toNumber(size);
    const list = (sizes || [])
      .map((s) => ({ value: String(s.value), available: Boolean(s.available), last: Boolean(s.last), n: toNumber(s.value) }))
      .filter((s) => !Number.isNaN(s.n))
      .sort((a, b) => a.n - b.n);
    return list.find((s) => s.n > n && s.available) || null;
  }

  // « Je fais du 42 en Nike » → pointure dans une autre marque, en passant par la longueur du pied
  function snkConvert(fromRows, fromSize, toRows) {
    const cm = snkLengthFromSize(fromRows, fromSize);
    return cm == null ? null : snkSizeFromLength(toRows, cm);
  }

  // Recommandation pour une longueur de pied saisie (cm) sur un modèle :
  // { status: 'out' } hors du tableau ; { status: 'above', size } pointure au-delà des tailles du modèle ;
  // { status: 'ok', size, between, exists, wanted, last } ; { status: 'sold_out', size, exists, wanted } : jamais
  // d'autre taille proposée à la place d'une taille épuisée. Sans liste de tailles (page), la pointure du tableau.
  function snkRecommend(rows, cm, sizes) {
    if (cm == null || Number.isNaN(cm)) return null;
    const found = snkSizeFromLength(rows, cm);
    if (!found) return { status: 'out' };
    if (!sizes || !sizes.length) return { status: 'ok', size: found.size, between: found.between, exists: true, last: false };
    const fit = snkFitToModel(found.size, sizes);
    // Pointure du tableau absente du modèle : on le dit, jamais la taille au-dessus (LG, 5 nov. 2026)
    if (!fit || !fit.exists) return { status: 'missing', size: found.size, between: found.between };
    const base = { size: fit.size, exists: true, wanted: null };
    return fit.available ? { status: 'ok', ...base, between: found.between, last: fit.last } : { status: 'sold_out', ...base };
  }

  const rules = { toNumber, snkSizeFromLength, snkLengthFromSize, snkFitToModel, snkNextAvailable, snkConvert, snkRecommend };
  if (typeof module !== 'undefined' && module.exports) module.exports = rules;
  if (typeof window !== 'undefined') window.snkSizeRules = rules;
  if (typeof customElements === 'undefined' || customElements.get('snk-size-guide')) return;

  const fmt = (text, vars) => Object.entries(vars).reduce((s, [k, v]) => s.split(`__${k}__`).join(v), text);
  const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  const same = (a, b) => String(a).trim().toLowerCase() === String(b).trim().toLowerCase();
  // Pointures avec un point (« 41.5 »), tiers Adidas tels quels ; longueurs de pied à la française (« 26,5 cm »)
  const sizeText = (size) => String(size).trim();
  const cmText = (cm) => `${cm.toFixed(1).replace('.', ',')} cm`;
  const str = (v) => (v == null || v === '' ? '' : String(v));
  const decimals = (v) => (String(v).split(/[.,]/)[1] || '').length;
  const num = (v) => Number(String(v).replace(',', '.'));
  // « 7.0 » (US Topper) → « 7 » : mise en forme seulement, la donnée reste telle quelle
  const plain = (v) => String(v).replace(/^(\d+)\.0+$/, '$1');
  const brandLabel = (b) => (same(b, 'New Balance') ? 'NB' : b);
  // Colonnes facultatives du tableau, dans l'ordre demandé : pied (cm), EU, BR, US, UK (codes identiques dans les 4 langues)
  const EXTRA_COLS = [
    ['br', 'BR'],
    ['us', 'US'],
    ['uk', 'UK'],
  ];

  customElements.define(
    'snk-size-guide',
    class SnkSizeGuide extends HTMLElement {
      // Aucune exception possible, quelle que soit la fiche : JSON absent ou incomplet, marque sans tableau,
      // pas de variantes, ancien balisage. En cas de pépin, le composant reste inerte et la fenêtre s'ouvre quand même.
      connectedCallback() {
        if (this.ready) return;
        this.ready = false;
        try {
          this.init();
          this.ready = true;
        } catch (error) {
          if (window.console) console.warn('snk-size-guide : initialisation impossible', error);
        }
      }

      init() {
        let data = {};
        try {
          data = JSON.parse(this.querySelector('[data-snk-sg-data]')?.textContent || '{}') || {};
        } catch (error) {
          data = {};
        }
        this.data = {
          mode: data.mode || this.dataset.mode || 'page',
          brand: data.brand || '',
          model: data.model || '',
          chosen: data.chosen || '',
          brandList: Array.isArray(data.brandList) ? data.brandList : [],
          charts: (Array.isArray(data.charts) ? data.charts : []).filter((c) => c && Array.isArray(c.rows)),
          sizes: Array.isArray(data.sizes) ? data.sizes : [],
          links: data.links || {},
          texts: data.texts || {},
        };
        this.t = new Proxy(this.data.texts, { get: (t, k) => (typeof k === 'string' ? t[k] ?? '' : t[k]) });
        this.mode = this.data.mode;
        // Marques : liste du réglage + celles qui ont un tableau + la marque du modèle, sans doublon
        const withChart = [...new Set(this.data.charts.map((c) => c.brand).filter(Boolean))];
        const list = [...this.data.brandList, ...withChart, this.data.brand].filter(Boolean);
        this.brandsAll = list.filter((b, i) => list.findIndex((x) => same(x, b)) === i).map((b) => withChart.find((c) => same(c, b)) || b);
        this.brands = this.brandsAll.filter((b) => this.chartFor(b));
        // Marque cible : celle du modèle sur la fiche ; sur la page, les pointures de toutes les marques
        this.target = this.mode === 'product' ? this.data.brand : null;

        this.panels = Object.fromEntries([...this.querySelectorAll('[data-snk-sg-panel]')].map((p) => [p.dataset.snkSgPanel, p]));
        this.tabs = [...this.querySelectorAll('[data-snk-sg-tab]')];
        this.tabs.forEach((tab) => tab.addEventListener('click', () => this.show(tab.dataset.snkSgTab)));
        this.querySelector('[role="tablist"]')?.addEventListener('keydown', (event) => {
          if (!['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          const i = this.tabs.indexOf(document.activeElement);
          const next = event.key === 'Home' ? 0 : event.key === 'End' ? this.tabs.length - 1 : (i + (event.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length;
          this.tabs[next].focus();
          this.show(this.tabs[next].dataset.snkSgTab);
        });

        this.res = this.querySelector('[data-snk-sg-res]') || document.createElement('div');
        this.idle = this.querySelector('[data-snk-sg-idle]');
        this.res.addEventListener('click', (event) => {
          const button = event.target.closest('[data-snk-sg-choose]');
          if (button) this.choose(button.dataset.snkSgChoose);
        });

        // État : marque et pointure portées (« Ma pointure »), longueur du pied (cm), source de la saisie (null : rien saisi)
        this.state = { brand: null, size: null, cm: null, unit: 'eu', source: null, tableBrand: null };
        this.setupUsual();
        this.setupMeasure();
        this.setupTable();
        this.renderLegal();
        this.render();
      }

      // Tableau d'une marque : sur la fiche, celui choisi par Liquid pour la marque du modèle ; sinon le plus récent
      chartFor(brand) {
        if (!brand) return null;
        if (this.mode === 'product' && same(brand, this.data.brand) && this.data.chosen) {
          const chosen = this.data.charts.find((c) => c.handle === this.data.chosen);
          if (chosen) return chosen;
        }
        return this.data.charts.filter((c) => same(c.brand, brand)).sort((a, b) => b.year - a.year)[0] || null;
      }

      show(name) {
        if (!this.ready || !this.panels[name]) return;
        this.tabs.forEach((tab) => {
          const on = tab.dataset.snkSgTab === name;
          tab.setAttribute('aria-selected', String(on));
          tab.tabIndex = on ? 0 : -1;
        });
        Object.entries(this.panels).forEach(([key, panel]) => (panel.hidden = key !== name));
        if (name === 'table') this.renderTable();
      }

      // Appelé à chaque ouverture de la fenêtre : la saisie du visiteur est gardée telle quelle ; rien n'est repris de la
      // taille présélectionnée sur la fiche (première taille en stock tant que le visiteur n'a rien choisi)
      reset() {
        if (!this.ready) return;
        if (!this.querySelector('[role="tab"][aria-selected="true"]')) this.show('usual');
      }

      sizesOf(brand) {
        return this.rowsOf(brand).map((r) => r.size);
      }

      // Lignes d'une marque triées par pointure, avec les champs facultatifs du tableau :
      // uk / us (Adidas, Topper), br (Topper : pointure brésilienne), width_std / width_2e (New Balance : largeurs D et 2E en cm)
      rowsOf(brand) {
        const chart = this.chartFor(brand);
        if (!chart) return [];
        const extra = Object.fromEntries(
          (chart.rows || []).map((r) => [String(r.size), { uk: str(r.uk), us: str(r.us), br: str(r.br), width_std: str(r.width_std), width_2e: str(r.width_2e), minRaw: r.min, maxRaw: r.max }])
        );
        return sortRows(chart.rows)
          .sort((a, b) => a.n - b.n)
          .map((r) => ({ ...r, ...extra[r.size] }));
      }

      hasUnits(brand) {
        return this.rowsOf(brand).some((r) => r.uk || r.us);
      }

      // ===== Onglet « Ma pointure » : pastilles de marques puis de pointures, aucune pointure présélectionnée =====
      setupUsual() {
        this.brandsBox = this.querySelector('[data-snk-sg-brands]') || document.createElement('div');
        this.sizesBox = this.querySelector('[data-snk-sg-sizes]') || document.createElement('div');
        // Par défaut : une autre marque que celle du modèle (le cas d'usage), sinon la première qui a un tableau
        this.state.brand = this.brands.find((b) => this.mode !== 'product' || !same(b, this.data.brand)) || this.brands[0] || null;
        this.brandsBox.addEventListener('click', (event) => {
          const pill = event.target.closest('[data-snk-sg-brand]');
          if (!pill || pill.disabled) return;
          this.state.brand = pill.dataset.snkSgBrand;
          // La pointure portée n'a de sens que dans sa marque : on l'efface au changement de marque
          if (!this.sizesOf(this.state.brand).some((s) => same(s, this.state.size))) this.state.size = null;
          this.renderBrands();
          this.renderSizes();
          if (this.state.source === 'usual') this.setFromUsual();
        });
        this.unitsBox = this.querySelector('[data-snk-sg-units]');
        this.unitsBox?.addEventListener('click', (event) => {
          const pill = event.target.closest('[data-snk-sg-unit]');
          if (!pill) return;
          this.state.unit = pill.dataset.snkSgUnit;
          this.renderSizes();
        });
        this.sizesBox.addEventListener('click', (event) => {
          const pill = event.target.closest('[data-snk-sg-size]');
          if (!pill) return;
          this.state.size = pill.dataset.snkSgSize;
          this.state.source = 'usual';
          this.renderSizes();
          this.setFromUsual();
        });
        this.renderBrands();
        this.renderSizes();
      }

      renderBrands() {
        this.brandsBox.innerHTML = this.brandsAll
          .map((brand) => {
            const has = Boolean(this.chartFor(brand));
            const on = has && same(brand, this.state.brand);
            return `<button type="button" class="snk-sg__opt${on ? ' is-on' : ''}" data-snk-sg-brand="${esc(brand)}" aria-pressed="${on}"${has ? '' : ' disabled'}>${esc(brand)}${has ? '' : `<small>${esc(this.t.soon)}</small>`}</button>`;
          })
          .join('');
      }

      renderSizes() {
        const rows = this.rowsOf(this.state.brand);
        // Bascule EU / UK / US seulement pour une marque dont le tableau porte ces équivalents
        if (this.unitsBox) {
          const units = this.hasUnits(this.state.brand);
          if (!units) this.state.unit = 'eu';
          this.unitsBox.hidden = !units;
          this.unitsBox.innerHTML = units
            ? ['eu', 'uk', 'us']
                .filter((u) => u === 'eu' || rows.some((r) => r[u]))
                .map((u) => `<button type="button" class="snk-sg__unit${this.state.unit === u ? ' is-on' : ''}" data-snk-sg-unit="${u}" aria-pressed="${this.state.unit === u}">${u.toUpperCase()}</button>`)
                .join('')
            : '';
        }
        const label = (r) => (this.state.unit !== 'eu' && r[this.state.unit] ? r[this.state.unit] : sizeText(r.size));
        this.sizesBox.innerHTML = rows
          .map((r) => {
            const on = this.state.size != null && same(r.size, this.state.size);
            return `<button type="button" class="snk-sg__opt${on ? ' is-on' : ''}" data-snk-sg-size="${esc(r.size)}" aria-pressed="${on}">${esc(label(r))}</button>`;
          })
          .join('');
      }

      // Marque + pointure portées → longueur du pied → recommandation
      setFromUsual() {
        const chart = this.chartFor(this.state.brand);
        const cm = chart && this.state.size ? snkLengthFromSize(chart.rows, this.state.size) : null;
        this.state.cm = cm;
        if (cm != null) {
          this.slider.value = cm;
          if (this.cmValue) this.cmValue.textContent = cmText(cm);
        }
        this.render();
      }

      // ===== Onglet « Je mesure mon pied » : curseur 22,0 → 31,0 cm au dixième ; rien n'est compté avant le 1er geste =====
      setupMeasure() {
        this.slider = this.querySelector('[data-snk-sg-cm]') || Object.assign(document.createElement('input'), { type: 'range', min: 22, max: 31, step: 0.1, value: 27 });
        this.cmValue = this.querySelector('[data-snk-sg-cmv]');
        this.slider.addEventListener('input', () => this.setCm(parseFloat(this.slider.value)));
        this.querySelectorAll('[data-snk-sg-step]').forEach((button) =>
          button.addEventListener('click', () => {
            const next = Math.min(31, Math.max(22, Math.round((parseFloat(this.slider.value) + Number(button.dataset.snkSgStep) * 0.1) * 10) / 10));
            this.slider.value = next;
            this.setCm(next);
          })
        );
      }

      // Saisie au curseur : la longueur pilote seule la recommandation ; la pointure de « Ma pointure » est effacée
      // (pas de deuxième pointure affichée ailleurs)
      setCm(cm) {
        this.state.cm = Math.round(cm * 10) / 10;
        this.state.source = 'measure';
        if (this.cmValue) this.cmValue.textContent = cmText(this.state.cm);
        if (this.state.size != null) {
          this.state.size = null;
          this.renderSizes();
        }
        this.render();
      }

      // ===== Onglet « Tableau » : une marque à la fois, seulement ses lignes =====
      setupTable() {
        this.table = this.querySelector('[data-snk-sg-table]') || document.createElement('table');
        this.tableBrandsBox = this.querySelector('[data-snk-sg-tbrands]');
        this.wide = this.querySelector('[data-snk-sg-wide]');
        this.wideTable = this.querySelector('[data-snk-sg-wtable]');
        const own = this.mode === 'product' ? this.brands.find((b) => same(b, this.data.brand)) : null;
        this.state.tableBrand = own || this.brands[0] || null;
        this.tableBrandsBox?.addEventListener('click', (event) => {
          const pill = event.target.closest('[data-snk-sg-tbrand]');
          if (!pill) return;
          this.state.tableBrand = pill.dataset.snkSgTbrand;
          this.renderTable();
        });
      }

      renderTable() {
        const brand = this.state.tableBrand;
        if (this.tableBrandsBox) {
          this.tableBrandsBox.hidden = this.brands.length < 2;
          this.tableBrandsBox.innerHTML = this.brands
            .map((b) => {
              const on = same(b, brand);
              return `<button type="button" class="snk-sg__chip${on ? ' is-on' : ''}" data-snk-sg-tbrand="${esc(b)}" aria-pressed="${on}">${esc(brandLabel(b))}</button>`;
            })
            .join('');
        }
        const rows = this.rowsOf(brand);
        if (!rows.length) {
          this.table.innerHTML = '';
          if (this.wide) this.wide.hidden = true;
          return;
        }
        // Une colonne par numérotation présente dans les données de la marque ; aucune valeur inventée
        const cols = EXTRA_COLS.filter(([key]) => rows.some((r) => r[key]));
        // Même nombre de décimales sur toute la colonne cm (Topper au centième, les autres au dixième)
        const dec = Math.min(2, Math.max(1, ...rows.map((r) => Math.max(decimals(r.minRaw), decimals(r.maxRaw)))));
        const cm = (v) => num(v).toFixed(dec).replace('.', ',');
        const reco = this.recommendedIn(brand);
        const isReco = (r) => reco != null && same(r.size, reco);
        const mark = `<span class="visually-hidden"> · ${esc(this.t.recommended)}</span>`;
        this.table.innerHTML = `<thead><tr><th scope="col">${esc(this.t.foot_cm)}</th><th scope="col">EU</th>${cols.map(([, label]) => `<th scope="col">${label}</th>`).join('')}</tr></thead>
          <tbody>${rows
            .map((r) => `<tr${isReco(r) ? ' class="is-reco"' : ''}><th scope="row">${cm(r.minRaw)}–${cm(r.maxRaw)}</th><td>${esc(sizeText(r.size))}${isReco(r) ? mark : ''}</td>${cols.map(([key]) => `<td>${esc(plain(r[key]))}</td>`).join('')}</tr>`)
            .join('')}</tbody>`;
        // Ligne conseillée visible dans le cadre du tableau seulement (la page et la fenêtre ne bougent pas)
        const row = this.table.querySelector('.is-reco');
        const wrap = this.table.closest('.snk-sg__tablewrap');
        if (row && wrap) {
          const head = this.table.tHead?.offsetHeight || 0;
          const top = row.offsetTop - head;
          if (top < wrap.scrollTop || row.offsetTop + row.offsetHeight > wrap.scrollTop + wrap.clientHeight) wrap.scrollTop = Math.max(0, top - row.offsetHeight);
        }
        this.table.setAttribute('aria-label', `${this.t.tab_table} · ${brand}`);
        this.renderWide(rows);
      }

      // Pointure à surligner dans le tableau d'une marque : rien tant que le visiteur n'a rien saisi ; sur la fiche, pour la
      // marque du modèle, la même pointure que le bloc de recommandation (taille au-dessus si absente du modèle)
      recommendedIn(brand) {
        const cm = this.state.source ? this.state.cm : null;
        const chart = this.chartFor(brand);
        if (cm == null || !chart) return null;
        if (this.mode === 'product' && same(brand, this.target)) {
          const reco = snkRecommend(chart.rows, cm, this.data.sizes);
          return reco && reco.status !== 'out' ? reco.size : null;
        }
        return snkSizeFromLength(chart.rows, cm)?.size ?? null;
      }

      // « Pied large ? » : largeurs D (standard) et 2E (large) par pointure, pour les marques qui les donnent (New Balance)
      renderWide(rows) {
        if (!this.wide) return;
        const wide = rows.filter((r) => r.width_std || r.width_2e);
        this.wide.hidden = !wide.length;
        if (!wide.length || !this.wideTable) return;
        const w = (v) => (v === '' ? '' : num(v).toFixed(1).replace('.', ','));
        this.wideTable.innerHTML = `<thead><tr><th scope="col">EU</th><th scope="col">${esc(this.t.width_std_cm)}</th><th scope="col">${esc(this.t.width_2e_cm)}</th></tr></thead>
          <tbody>${wide.map((r) => `<tr><th scope="row">${esc(sizeText(r.size))}</th><td>${w(r.width_std)}</td><td>${w(r.width_2e)}</td></tr>`).join('')}</tbody>`;
      }

      renderLegal() {
        const parts = this.brands.map((b) => {
          const c = this.chartFor(b);
          const label = c.provisional ? this.t.provisional : this.t.official;
          return c.source ? `<a href="${esc(c.source)}" target="_blank" rel="noopener">${esc(b)}</a> (${esc(label)})` : `${esc(b)} (${esc(label)})`;
        });
        const legal = this.querySelector('[data-snk-sg-legal]');
        if (legal) legal.innerHTML = parts.length ? `${esc(this.t.source)} ${parts.join(' · ')}` : '';
      }

      // ===== Bloc de recommandation : un seul, affiché seulement après une saisie =====
      render() {
        const cm = this.state.source ? this.state.cm : null;
        if (cm == null) {
          this.res.innerHTML = '';
          if (this.idle) this.idle.hidden = false;
          return;
        }
        if (this.idle) this.idle.hidden = true;
        const kicker = this.mode === 'product' ? fmt(this.t.for_pair, { MODEL: this.data.model }) : this.t.page_kicker;
        const link = (href, label) =>
          !href ? '' : `<a class="snk-sg__res-link" href="${esc(href)}"${String(href).startsWith('http') ? ' target="_blank" rel="noopener"' : ''}>${esc(label)} →</a>`;
        const block = (big, text, state = '', extra = '') =>
          `<span class="snk-label snk-sg__res-k">${esc(kicker)}</span>
           <div class="snk-sg__rr"><b class="snk-sg__rsz">${big}</b><div><b class="snk-sg__rtx">${esc(text)}</b>${state ? `<span class="snk-sg__rst">${state}</span>` : ''}</div></div>${extra}`;

        // Page : ton pied, puis la pointure marque par marque (les marques où la longueur est hors tableau sont omises)
        if (this.mode === 'page') {
          const eq = this.brands
            .map((b) => ({ brand: b, size: snkSizeFromLength(this.chartFor(b).rows, cm)?.size }))
            .filter((e) => e.size)
            .map((e) => `<div><span>${esc(brandLabel(e.brand))}</span><b>${esc(sizeText(e.size))}</b></div>`)
            .join('');
          this.res.innerHTML = block(`${esc(cm.toFixed(1).replace('.', ','))}<small>cm</small>`, this.t.your_foot, '', eq ? `<div class="snk-sg__eq">${eq}</div>` : '');
          return;
        }

        const chart = this.chartFor(this.target);
        if (!chart) {
          this.res.innerHTML = block('?', this.t.no_chart.split(':')[0], esc(this.t.no_chart), link(this.data.links.dm, this.t.dm));
          return;
        }
        const reco = snkRecommend(chart.rows, cm, this.data.sizes);
        if (!reco || reco.status === 'out') {
          this.res.innerHTML = block('—', this.t.out_of_range.split(':')[0], esc(this.t.out_of_range), link(this.data.links.contact, this.t.contact));
          return;
        }
        const size = sizeText(reco.size);
        // Paires dispo dans cette pointure (toutes les chaussures, filtre « En stock » + pointure)
        const sizeLink = this.data.links.size ? link(`${this.data.links.size}${encodeURIComponent(reco.size)}`, fmt(this.t.see_size, { SIZE: size })) : '';
        if (reco.status === 'missing') {
          // Pas dans ce modèle : la pointure du tableau, « Pas dispo sur ce modèle », jamais une autre taille
          this.res.innerHTML = block(esc(size), fmt(this.t.take, { SIZE: size }), esc(this.t.not_available), sizeLink);
          return;
        }
        if (reco.status === 'sold_out') {
          // Épuisée : on le dit, sans proposer une autre taille
          this.res.innerHTML = block(esc(size), fmt(this.t.sold_out_size, { SIZE: size }), '', sizeLink);
          return;
        }
        let state = reco.between ? esc(this.t.between) : esc(this.t.available);
        if (reco.last) state = esc(this.t.last_pair);
        const button = this.closest('dialog')
          ? `<button type="button" class="snk-btn snk-btn--lime snk-btn--full snk-sg__choose" data-snk-sg-choose="${esc(reco.size)}">${esc(fmt(this.t.choose, { SIZE: size }))}</button>`
          : '';
        this.res.innerHTML = block(esc(size), fmt(this.t.take, { SIZE: size }), state, button);
      }

      // « Choisir le 43 » : ferme la fenêtre puis choisit la taille par le menu de la fiche (<snk-size-select>), qui la
      // marque comme choisie ; si elle n'est dispo que dans une autre boîte, le menu bascule aussi la boîte (alt)
      choose(size) {
        this.closest('dialog')?.close();
        const select = this.closest('snk-product-buy')?.querySelector('snk-size-select') || document.querySelector('snk-size-select');
        if (!select?.choose) return;
        const item = [...select.querySelectorAll('[data-snk-size-value]')].find((el) => el.dataset.snkSizeValue === String(size));
        select.choose(size, item?.dataset.snkSizeAlt || '');
      }
    }
  );
})();
